/**
 * Lê duração e dimensões de um vídeo local, sem decodificá-lo: um
 * `<video>` com `preload="metadata"` basta e custa alguns KB de leitura.
 *
 * O planejamento de compressão depende da DURAÇÃO (bitrate = bytes/segundo) e
 * das dimensões (para nunca ampliar). Sem isso não há como decidir quanto
 * comprimir, e o plano cai em `impossivel` com motivo explícito.
 */

export interface MetadadosDeVideo {
    duracaoSec: number;
    larguraPx: number | null;
    alturaPx: number | null;
}

/** Quanto esperar pelos metadados antes de desistir. Arquivo local resolve em
 *  milissegundos; este teto só existe para não pendurar o envio num arquivo
 *  corrompido que nunca dispara `loadedmetadata` nem `error`. */
const TIMEOUT_MS = 15_000;

export function lerMetadadosDeVideo(blob: Blob): Promise<MetadadosDeVideo> {
    return new Promise<MetadadosDeVideo>((resolve, reject) => {
        const url = URL.createObjectURL(blob);
        const video = document.createElement('video');
        let encerrado = false;

        const encerrar = (fn: () => void) => {
            if (encerrado) return;
            encerrado = true;
            clearTimeout(timer);
            video.onloadedmetadata = null;
            video.onerror = null;
            URL.revokeObjectURL(url);
            video.removeAttribute('src');
            fn();
        };

        const timer = setTimeout(
            () => encerrar(() => reject(new Error('Tempo esgotado ao ler os metadados do vídeo.'))),
            TIMEOUT_MS,
        );

        video.onloadedmetadata = () => {
            // `Infinity` acontece em WebM de gravador, cujo header não declara
            // duração. Quem chama trata como duração desconhecida.
            const duracaoSec = Number.isFinite(video.duration) ? video.duration : 0;
            const larguraPx = video.videoWidth || null;
            const alturaPx = video.videoHeight || null;
            encerrar(() => resolve({ duracaoSec, larguraPx, alturaPx }));
        };

        video.onerror = () =>
            encerrar(() =>
                reject(new Error('Não foi possível ler este vídeo. O arquivo pode estar corrompido.')),
            );

        video.preload = 'metadata';
        video.muted = true;
        video.src = url;
    });
}
